/* global I18n */
import './styles.scss'

import PropTypes from 'prop-types'

import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import Imgix from 'lib/tags/imgix'
import railsContext from 'lib/selectors/rails-context'
import Carousel from 'lib/components/carousel'

import { cameraIcon, closeIcon, profileIcon, uploadIcon } from '../_assets/icons-map'
import { ACCEPTED_EXTENSIONS } from '../constants.js'

const FIRST_SLIDE_AR = 3
const FIRST_SLIDE_LA = 0

const MobileProfilePhoto = React.forwardRef(
  (
    {
      avatar,
      closeModal,
      fileValue,
      handleChangeUploadImage,
      handleInputWebcamCapture,
      handleUploadImage,
      handleWebcam,
      withIconsUi
    },
    ref
  ) => {
    const { dpr, i18nLocale } = useSelector(railsContext)
    const firstSlideIndex = i18nLocale === 'ar' ? FIRST_SLIDE_AR : FIRST_SLIDE_LA
    const { uploadImageInputRef, webcamInputRef } = ref
    const [activeSlideIndex, setActiveSlideIndex] = useState(firstSlideIndex)

    return (
      <div className={`mobile-profile-photo${avatar ? ' uploaded-avatar' : ''}`}>
        {avatar && <img src={closeIcon} alt='close-icon' className='close-icon' onClick={closeModal} />}
        <p className='title'>{I18n.t('profiles.edit.avatar')}</p>
        <p className='subtitle'>{I18n.t('popups.profile_photo.subtitle')}</p>
        <div className='sections-container'>
          <div className='picture-section'>
            <div className='picture-container'>
              {i18nLocale === 'ar' ? (
                <Carousel
                  containerProps={{
                    className: 'container-slider'
                  }}
                  innerProps={{
                    className: `container-slider-inner${avatar ? ' disable-animation' : ''}`
                  }}
                  autoplayDelay={3500}
                  autoplay={!avatar}
                  disableSwipeByMouse={avatar}
                  disableSwipeByTouch={avatar}
                  activeSlideIndex={activeSlideIndex}
                  onRequestChange={setActiveSlideIndex}
                  infinite
                  easing={'ease-out'}
                  speed={750}
                  autoplayDirection={'backward'}
                  centerMode
                  swipeTreshold={49.6}
                  forwardBtnProps={{
                    show: false
                  }}
                  backwardBtnProps={{
                    show: false
                  }}
                  itemsListProps={{
                    className: 'items-list'
                  }}
                  dotsNav={{
                    show: !avatar,
                    itemBtnProps: {
                      className: 'item-button'
                    },
                    activeItemBtnProps: {
                      className: 'active-item-button'
                    },
                    containerProps: {
                      className: 'dots-nav-container'
                    }
                  }}
                >
                  {!avatar && (
                    <div className='item'>
                      <div className='other-tips-container'>
                        <p className='other-tips'>{I18n.t('popups.profile_photo.other_tips')}</p>
                        <div className='faces'>
                          <div className='face valid valid-3'></div>
                          <div className='face valid valid-4'></div>
                          <div className='face invalid invalid-3'></div>
                          <div className='face invalid invalid-4'></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!avatar && (
                    <div className='item'>
                      <div className='other-tips-container'>
                        <p className='other-tips'>{I18n.t('popups.profile_photo.other_tips')}</p>
                        <div className='faces'>
                          <div className='face valid valid-1'></div>
                          <div className='face valid valid-2'></div>
                          <div className='face invalid invalid-1'></div>
                          <div className='face invalid invalid-2'></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!avatar && (
                    <div className='item rtl'>
                      <div className='tips-container'>
                        <p className='tips-name'>{I18n.t('popups.profile_photo.tips_name')}</p>
                        <ul className='tips'>
                          <li>{I18n.t('popups.profile_photo.tip_1')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_2')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_3')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_4')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_5')}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className='item rtl'>
                    <div className='profile-container'>
                      {avatar ? (
                        <Imgix
                          src={avatar}
                          alt='Avatar'
                          dpr={dpr}
                          passThrough={true}
                          width={200}
                          height={200}
                          className='avatar-image'
                        />
                      ) : (
                        <img src={profileIcon} alt='profile-icon' className='profile-icon' />
                      )}
                    </div>
                    <div className='profile-picture-requirements-container'>
                      <ul className='profile-picture-requirements'>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_1')}</li>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_2')}</li>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_3')}</li>
                      </ul>
                    </div>
                  </div>
                </Carousel>
              ) : (
                <Carousel
                  containerProps={{
                    className: 'container-slider'
                  }}
                  innerProps={{
                    className: `container-slider-inner${avatar ? ' disable-animation' : ''}${
                      activeSlideIndex !== 1 ? ' withHeight' : ''
                    }`
                  }}
                  autoplayDelay={3500}
                  autoplay={!avatar}
                  disableSwipeByMouse={avatar}
                  disableSwipeByTouch={avatar}
                  infinite
                  easing={'ease-out'}
                  speed={750}
                  onAfterChange={currentSlideIndex => {
                    setActiveSlideIndex(currentSlideIndex)
                  }}
                  autoplayDirection={'forward'}
                  centerMode
                  swipeTreshold={49.6}
                  forwardBtnProps={{
                    show: false
                  }}
                  backwardBtnProps={{
                    show: false
                  }}
                  itemsListProps={{
                    className: 'items-list'
                  }}
                  dotsNav={{
                    show: !avatar,
                    itemBtnProps: {
                      className: 'item-button'
                    },
                    activeItemBtnProps: {
                      className: 'active-item-button'
                    },
                    containerProps: {
                      className: 'dots-nav-container'
                    }
                  }}
                >
                  <div className='item'>
                    <div className='profile-container'>
                      {avatar ? (
                        <Imgix
                          src={avatar}
                          alt='Avatar'
                          dpr={dpr}
                          passThrough={true}
                          width={200}
                          height={200}
                          className='avatar-image'
                        />
                      ) : (
                        <img src={profileIcon} alt='profile-icon' className='profile-icon' />
                      )}
                    </div>
                    <div className='profile-picture-requirements-container'>
                      <ul className='profile-picture-requirements'>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_1')}</li>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_2')}</li>
                        <li className='requirement'>{I18n.t('popups.profile_photo.requirement_3')}</li>
                      </ul>
                    </div>
                  </div>
                  {!avatar && (
                    <div className='item'>
                      <div className='tips-container'>
                        <p className='tips-name'>{I18n.t('popups.profile_photo.tips_name')}</p>
                        <ul className='tips'>
                          <li>{I18n.t('popups.profile_photo.tip_1')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_2')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_3')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_4')}</li>
                          <li>{I18n.t('popups.profile_photo.tip_5')}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  {!avatar && (
                    <div className='item'>
                      <div className='other-tips-container'>
                        <p className='other-tips'>{I18n.t('popups.profile_photo.other_tips')}</p>
                        <div className='faces'>
                          <div className='face valid valid-1'></div>
                          <div className='face valid valid-2'></div>
                          <div className='face invalid invalid-1'></div>
                          <div className='face invalid invalid-2'></div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!avatar && (
                    <div className='item'>
                      <div className='other-tips-container'>
                        <p className='other-tips'>{I18n.t('popups.profile_photo.other_tips')}</p>
                        <div className='faces'>
                          <div className='face valid valid-3'></div>
                          <div className='face valid valid-4'></div>
                          <div className='face invalid invalid-3'></div>
                          <div className='face invalid invalid-4'></div>
                        </div>
                      </div>
                    </div>
                  )}
                </Carousel>
              )}
              <div className='buttons-section'>
                <div className='take-a-picture-container' onClick={handleWebcam}>
                  <div className='camera-icon-container'>
                    <img src={cameraIcon} alt='camera-icon' className='camera-icon' />
                  </div>
                  <p className='camera-text'>{I18n.t('bookings.contact_details.take_picture')}</p>
                  {withIconsUi && (
                    <input
                      ref={webcamInputRef}
                      type='file'
                      id='webcam-input'
                      className='webcam-input'
                      capture='user'
                      accept='image/jpeg, image/jpg, image/gif, image/png'
                      onChange={handleInputWebcamCapture}
                    />
                  )}
                </div>
                <div className='upload-image-container' onClick={handleUploadImage}>
                  <div className='upload-icon-container'>
                    <img src={uploadIcon} alt='upload-icon' className='upload-icon' />
                  </div>
                  <p className='upload-text'>{I18n.t('activerecord.attributes.user.avatar_format_custom')}</p>
                  <input
                    className='upload-form'
                    type='file'
                    ref={uploadImageInputRef}
                    accept={ACCEPTED_EXTENSIONS.join(',')}
                    onChange={handleChangeUploadImage}
                    value={fileValue}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

MobileProfilePhoto.displayName = 'MobileProfilePhoto'

MobileProfilePhoto.propTypes = {
  handleWebcam: PropTypes.func,
  handleUploadImage: PropTypes.func,
  handleChangeUploadImage: PropTypes.func,
  closeModal: PropTypes.func,
  handleInputWebcamCapture: PropTypes.func,
  avatar: PropTypes.string,
  fileValue: PropTypes.string,
  withIconsUi: PropTypes.bool
}

export default MobileProfilePhoto
